import type { DataPoint } from '@/types/services/common'
import type {
  ClientGnavData,
  EntityGnavData,
} from '@/types/services/performance'

/**
 * Normalizes a GNAV DataPoint x-value into a timestamp in milliseconds.
 *
 * @param item - DataPoint with x stored as number, ISO string, or Date
 * @returns Timestamp in milliseconds
 */
export function getDataPointTime(item: DataPoint): number {
  if (typeof item.x === 'number') {
    return item.x
  }

  if (typeof item.x === 'string') {
    return new Date(item.x).getTime()
  }

  return item.x.getTime()
}

/**
 * Parses GNAV snapshots from API response into DataPoint array.
 *
 * @param snapshots - Array of GNAV data from API
 * @param getEntityDate - Function to convert date to entity timezone
 * @param useNetPrices - When false, chart `gross_value` (NAV before today's
 *   fee) instead of `value`. Client snapshots carry no gross figure, so they
 *   stay net regardless.
 * @returns Array of DataPoints with x (timestamp) and y (value)
 */
export function parseGnavSnapshots(
  snapshots: (ClientGnavData | EntityGnavData)[],
  getEntityDate: (date: Date) => Date,
  useNetPrices: boolean = true
): DataPoint[] {
  return snapshots
    .map((record) => {
      const date = new Date(record.date)
      const x = getEntityDate(date)
      let y = record.value
      if (!useNetPrices && 'gross_value' in record) {
        y = record.gross_value
      }
      return {
        x: x.getTime(),
        y,
      }
    })
    .sort((a, b) => a.x - b.x)
}

/**
 * Gets the last (most recent) value from GNAV data.
 *
 * @param gnavData - Array of GNAV DataPoints
 * @returns Last value or null if empty
 */
export function getLastGnavValue(gnavData: DataPoint[]): number | null {
  if (!gnavData.length) {
    return null
  }
  return gnavData[gnavData.length - 1].y
}

/**
 * Gets the GNAV value at-or-before the given timestamp.
 * Assumes gnavData is sorted ascending by x.
 *
 * @param gnavData - Array of GNAV DataPoints
 * @param timestamp - Cutoff timestamp in milliseconds
 * @returns Value of the latest point with x <= timestamp, or null if none
 */
export function getGnavValueAt(
  gnavData: DataPoint[],
  timestamp: number
): number | null {
  for (let i = gnavData.length - 1; i >= 0; i--) {
    if (getDataPointTime(gnavData[i]) <= timestamp) {
      return gnavData[i].y
    }
  }
  return null
}

/**
 * Aggregates multiple GNAV data series into a single series by summing values at each timestamp.
 * Uses forward-fill interpolation for missing values (carries forward the last known value).
 *
 * @param dataSets - Array of DataPoint arrays to aggregate
 * @returns Aggregated DataPoint array with summed values
 */
export function aggregateGnavDataSets(dataSets: DataPoint[][]): DataPoint[] {
  if (dataSets.length === 0) return []
  if (dataSets.length === 1) return [...dataSets[0]]

  // Collect all unique timestamps
  const allTimestamps = new Set<number>()
  for (const dataSet of dataSets) {
    for (const point of dataSet) {
      if (typeof point.x !== 'number') {
        throw new Error('DataPoint x value must be a number (timestamp)')
      }

      allTimestamps.add(point.x)
    }
  }

  // Sort timestamps
  const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b)

  if (sortedTimestamps.length === 0) return []

  // Create maps for quick lookup and track last known values
  const dataSetMaps: Map<number, number>[] = dataSets.map((ds) => {
    const map = new Map<number, number>()
    for (const point of ds) {
      if (typeof point.x !== 'number') {
        throw new Error('DataPoint x value must be a number (timestamp)')
      }

      map.set(point.x, point.y)
    }
    return map
  })

  // Aggregate values at each timestamp
  const result: DataPoint[] = []
  const lastKnownValues: (number | null)[] = dataSets.map(() => null)

  for (const timestamp of sortedTimestamps) {
    let sum = 0
    let hasAnyValue = false

    for (let i = 0; i < dataSetMaps.length; i++) {
      const value = dataSetMaps[i].get(timestamp)
      if (value !== undefined) {
        lastKnownValues[i] = value
        sum += value
        hasAnyValue = true
      } else if (lastKnownValues[i] !== null) {
        // Use last known value (forward-fill)
        sum += lastKnownValues[i]!
        hasAnyValue = true
      }
    }

    if (hasAnyValue) {
      result.push({ x: timestamp, y: sum })
    }
  }

  return result
}
